import { openDB } from 'idb';

/**
 * @typedef {object} PhotoUploadFailure
 * @property {string} code The latest failure's error code, or its error name when it has no code.
 * @property {string} message The latest failure's full error message, sent to support with the photo data.
 * @property {number} latestFailureAt When the latest failure happened.
 * @property {number} consecutiveFailureCount Failed attempts in a row since an attempt last got through.
 */

const QUEUE_STORE = 'queue';

/** @typedef {Object} NodeAssociationConfig
 * @property {string} nodeId
 */

/** @typedef {Object} SectionAssociationConfig
 * @property {string} connectionId
 * @property {string} sectionId
 */

/** @typedef {NodeAssociationConfig | SectionAssociationConfig} AssociationConfig */

/**
 * Metadata to store with the photo upload.
 * @typedef {Record<string, any>} Metadata
 */

/**
 * An update to apply to ...{jobId}/photos/{photoId} after upload.
 * @typedef {Record<string, any>} PhotoUpdate
 */

/**
 * An update object to apply to ...{jobId}/nodes/{nodeId}/photos/{photoId} or ...{jobId}/connections/{connectionId}/sections/{sectionId}/photos/{photoId} after upload.
 * @typedef {Record<string, any>} AssociatedPhotoUpdate
 */

/** @typedef {Object} QueueRecordData
 *  @property {string} jobId
 *  @property {string} photoId
 *  @property {string} filetype
 *  @property {string} filename
 *  @property {string} uploadedBy
 *  @property {number} createdAt
 *  @property {Metadata} [metadata]
 *  @property {PhotoUpdate} [photoUpdate]
 *  @property {AssociationConfig} [association]
 *  @property {AssociatedPhotoUpdate} [associatedPhotoUpdate]
 *  @property {Record<string, any>} [exif] The photo's EXIF, written to the job's photo data after upload.
 *  @property {boolean} [hasUploadedPhotoBytes] Whether the photo bytes have been successfully uploaded to Storage.
 *  @property {boolean} [hasSeenPhotoDataOnServer] Whether the photo data has been seen saved in the photo's upload state on the server.
 *  @property {boolean} [hasStagedPhotoData] Whether a tab on this device already staged the photo data for the update manager to sync.
 *  @property {PhotoUploadFailure|null} [uploadFailure] The latest run of failed attempts, or null when the latest attempt got through.
 */

/** @typedef {QueueRecordData & { id: string }} QueueRecord */

const QUEUE_RECORD_PROPERTIES = {
  jobId: { type: 'string' },
  filetype: { type: 'string' },
  filename: { type: 'string' },
  uploadedBy: { type: 'string' },
  createdAt: { type: 'number' },
  metadata: { type: 'object', optional: true },
  association: { type: 'object', optional: true },
  associatedPhotoUpdate: { type: 'object', optional: true },
  photoUpdate: { type: 'object', optional: true },
  exif: { type: 'object', optional: true },
  hasUploadedPhotoBytes: { type: 'boolean', optional: true },
  hasSeenPhotoDataOnServer: { type: 'boolean', optional: true },
  hasStagedPhotoData: { type: 'boolean', optional: true },
  uploadFailure: { type: 'object', optional: true }
};

async function openDatabase() {
  const db = await openDB('photo_uploads', 1, {
    upgrade(db) {
      const store = db.createObjectStore(QUEUE_STORE);
      store.createIndex('jobId', 'jobId');
    }
  });
  return db;
}

/**
 * Validates that a queue record has the required properties and types.
 * @param {QueueRecordData} queueRecordData
 * @throws {Error} If the queue record is missing required properties or has invalid types.
 */
function validateQueueRecordData(queueRecordData) {
  for (const [prop, { type, optional }] of Object.entries(QUEUE_RECORD_PROPERTIES)) {
    const propertyExists = queueRecordData[prop] != null;
    if (!propertyExists && !optional) {
      throw new Error(`Queue record is missing required property: ${prop}`);
    } else if (propertyExists && typeof queueRecordData[prop] !== type) {
      throw new Error(`Queue record property ${prop} must be of type ${type}`);
    }
  }
}

/**
 * Adds a photo upload record to the queue store.
 * @param {QueueRecordData} queueRecordData
 * @param {string} recordId The id the photo bytes were already stored under.
 * @returns {Promise<string>} The id of the added queue record.
 */
async function addQueueRecord(queueRecordData, recordId) {
  queueRecordData.createdAt = Date.now();
  validateQueueRecordData(queueRecordData);
  const db = await openDatabase();
  await db.add(QUEUE_STORE, queueRecordData, recordId);
  db.close();
  return recordId;
}

/**
 * Removes a photo upload record from the queue store.
 * @param {string} recordId
 * @return {Promise<void>}
 */
async function removeQueueRecord(recordId) {
  const db = await openDatabase();
  await db.delete(QUEUE_STORE, recordId);
  db.close();
}

/**
 * Merge fields onto a photo upload record when it is still queued.
 * @param {string} recordId
 * @param {Partial<QueueRecordData>} queueRecordDataUpdate The fields to write onto the record.
 * @returns {Promise<void>} Resolves once the record is updated or found no longer queued.
 */
async function updateQueueRecordData(recordId, queueRecordDataUpdate) {
  const database = await openDatabase();
  const transaction = database.transaction(QUEUE_STORE, 'readwrite');
  const queueRecordData = await transaction.store.get(recordId);
  const updatedQueueRecordData = { ...queueRecordData, ...queueRecordDataUpdate };
  if (queueRecordData) await transaction.store.put(updatedQueueRecordData, recordId);
  await transaction.done;
  database.close();
}

/**
 * Retrieves a photo upload record from the queue store.
 * @param {string} recordId
 * @returns {Promise<QueueRecord|null>} The retrieved queue record, or null when it is not in the queue.
 */
async function getQueueRecord(recordId) {
  const db = await openDatabase();
  /** @type {QueueRecordData} */
  const queueRecordData = await db.get(QUEUE_STORE, recordId);
  db.close();
  if (!queueRecordData) return null;
  return { ...queueRecordData, id: recordId };
}

/**
 * Retrieves all photo upload record ids from the queue store for a specific job.
 * @param {string} jobId
 * @returns {Promise<QueueRecord[]>} The retrieved queue records.
 */
async function getQueueRecordsForJob(jobId) {
  const db = await openDatabase();
  const index = db.transaction(QUEUE_STORE).store.index('jobId');
  /** @type {QueueRecord[]} */
  const queueRecords = [];
  for await (const cursor of index.iterate(jobId)) {
    const queueRecordData = /** @type {QueueRecordData} */ (cursor.value);
    const queueRecordId = /** @type {string} */ (cursor.primaryKey);
    const queueRecord = { ...queueRecordData, id: queueRecordId };
    queueRecords.push(queueRecord);
  }
  db.close();
  return queueRecords;
}

/**
 * Retrieves all photo upload record ids from the queue store for a specific job and node.
 * @param {string} jobId
 * @param {string} nodeId
 * @returns {Promise<QueueRecord[]>} The retrieved queue records.
 */
async function getQueueRecordsForNode(jobId, nodeId) {
  const jobRecords = await getQueueRecordsForJob(jobId);
  const nodeRecords = jobRecords.filter((record) => {
    return record.association != null && 'nodeId' in record.association && record.association.nodeId === nodeId;
  });
  return nodeRecords;
}

/**
 * Retrieves every photo upload record in the queue store.
 * @returns {Promise<QueueRecord[]>} The retrieved queue records.
 */
async function getAllQueueRecords() {
  const db = await openDatabase();
  const store = db.transaction(QUEUE_STORE).store;
  /** @type {QueueRecord[]} */
  const queueRecords = [];
  for await (const cursor of store.iterate()) {
    const queueRecordData = /** @type {QueueRecordData} */ (cursor.value);
    const queueRecordId = /** @type {string} */ (cursor.primaryKey);
    const queueRecord = { ...queueRecordData, id: queueRecordId };
    queueRecords.push(queueRecord);
  }
  db.close();
  return queueRecords;
}

/**
 * Retrieves all job ids that have associated photo upload records in the queue store.
 * @returns {Promise<string[]>} An array of job IDs.
 */
async function getJobIdsWithRecords() {
  const db = await openDatabase();
  const tx = db.transaction(QUEUE_STORE);
  const records = await tx.store.getAll();
  db.close();
  const jobIds = new Set(records.map((record) => record.jobId));
  return Array.from(jobIds);
}

export {
  addQueueRecord,
  removeQueueRecord,
  updateQueueRecordData,
  getQueueRecord,
  getAllQueueRecords,
  getQueueRecordsForJob,
  getQueueRecordsForNode,
  getJobIdsWithRecords
};
